/**
 * Greenfield-owned login parse. Accepts the legitimate development login-request
 * body (including optional devMagicLinkToken) without leaking raw Zod/API dumps.
 * Does not weaken production verify/session schemas.
 */

export function humanizeStudioLoginError(input: {
  readonly status?: number;
  readonly body?: unknown;
  readonly fallback?: string;
}): string {
  const body = input.body;
  const error =
    body && typeof body === "object" && "error" in body ? (body as { error?: unknown }).error : undefined;
  const errorText = typeof error === "string" ? error : "";
  if (input.status === 400 || errorText.includes("email")) {
    return "Enter a valid work email.";
  }
  if (input.status === 404 || errorText === "agency_user_not_found") {
    return "No Studio account matches that email.";
  }
  if (input.status === 409 || errorText === "agency_login_email_ambiguous") {
    return "That email matches more than one Studio account. Ask an owner to fix it.";
  }
  if (input.status === 401 || errorText === "agency_magic_link_invalid") {
    return "That sign-in link is invalid or expired. Request a new one.";
  }
  return input.fallback ?? "We could not send a sign-in link. Try again.";
}

export function parseAgencyLoginRequestResult(body: unknown): { readonly devMagicLinkToken?: string } {
  if (!body || typeof body !== "object") {
    throw new Error("We could not send a sign-in link. Try again.");
  }
  const rec = body as Record<string, unknown>;
  if (rec.ok !== true) {
    throw new Error("We could not send a sign-in link. Try again.");
  }
  const token = rec.devMagicLinkToken;
  return {
    devMagicLinkToken: typeof token === "string" && token.length >= 16 ? token : undefined,
  };
}

export async function requestGreenfieldAgencyLogin(
  apiBaseUrl: string,
  email: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ readonly devMagicLinkToken?: string }> {
  const base = apiBaseUrl.replace(/\/$/, "");
  const res = await fetchImpl(`${base}/v1/auth/agency/login-request`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const body: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(humanizeStudioLoginError({ status: res.status, body }));
  }
  return parseAgencyLoginRequestResult(body);
}
